import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Badge, Button, Space, Tag, Segmented,
  Empty, Spin, message, Tooltip,
} from 'antd';
import {
  HomeOutlined, CalendarOutlined, FileTextOutlined,
  DollarOutlined, FileProtectOutlined, ThunderboltOutlined,
  LeftOutlined, RightOutlined, AppstoreOutlined, UnorderedListOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import axiosInstance from '../utils/axios';
import { hssColors, commonStyles } from '../config/theme';
import Footer from '../components/Footer';

dayjs.locale('es');

// ── Configuración por tipo ────────────────────────────────────────────────────
const TIPO_CFG = {
  ORDEN:           { color: hssColors.primary, antColor: 'blue',  label: 'Orden',           icon: <FileTextOutlined /> },
  PROMESA_PAGO:    { color: '#faad14',          antColor: 'gold',  label: 'Promesa de pago', icon: <DollarOutlined /> },
  CONTRATO_VENCE:  { color: '#f5222d',          antColor: 'red',   label: 'Contrato vence',  icon: <FileProtectOutlined /> },
};

const TIPO_COLORS = {
  ORDEN:           { bg: '#eff6ff', border: '#bfdbfe', text: '#1e40af', accent: hssColors.primary },
  PROMESA_PAGO:    { bg: '#fffbeb', border: '#fde68a', text: '#92400e', accent: '#f59e0b' },
  CONTRATO_VENCE:  { bg: '#fef2f2', border: '#fecaca', text: '#991b1b', accent: '#f5222d' },
};

const PRIORIDAD_COLOR    = { BAJA: 'default', MEDIA: 'blue', ALTA: 'orange', URGENTE: 'red' };
const ESTADO_PROMESA_COLOR = { PENDIENTE: 'orange', CUMPLIDA: 'green', INCUMPLIDA: 'red' };

// ── Constantes de timeline ────────────────────────────────────────────────────
const HOUR_HEIGHT  = 64;
const FIRST_HOUR   = 7;
const LAST_HOUR    = 21;
const HOURS_DAY    = Array.from({ length: LAST_HOUR - FIRST_HOUR + 1 }, (_, i) => i + FIRST_HOUR);
const HOURS_WEEK   = Array.from({ length: 13 }, (_, i) => i + 7); // 07:00-19:00
const TOTAL_HEIGHT = (LAST_HOUR - FIRST_HOUR) * HOUR_HEIGHT;
const DAYS_HEADER  = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

// ── Helpers ───────────────────────────────────────────────────────────────────
const getMonday = (d) => {
  const dow = d.day();
  return d.add(dow === 0 ? -6 : 1 - dow, 'day');
};

// Hora efectiva del evento:
//   • sin horario o medianoche  → 8:00
//   • antes de las 7            → 7:00 (clamp al inicio del timeline)
const getEvtHour = (e) => {
  if (!e.fecha?.includes('T')) return 8;
  const d = dayjs(e.fecha);
  if (d.hour() === 0 && d.minute() === 0) return 8;
  return d.hour() < FIRST_HOUR ? FIRST_HOUR : d.hour();
};
const getEvtMinute = (e) => {
  if (!e.fecha?.includes('T')) return 0;
  const d = dayjs(e.fecha);
  if (d.hour() === 0 && d.minute() === 0) return 0;
  return d.hour() < FIRST_HOUR ? 0 : d.minute();
};

const irA = (evento, navigate) => {
  if (evento.tipo === 'ORDEN')               navigate('/ordenes');
  else if (evento.tipo === 'PROMESA_PAGO')   navigate(`/clientes/${evento.link_id}`);
  else if (evento.tipo === 'CONTRATO_VENCE') navigate('/contratos');
};

// ── Tarjeta de evento ─────────────────────────────────────────────────────────
const EventoCard = ({ evento, navigate: nav }) => {
  const cfg    = TIPO_CFG[evento.tipo]    || TIPO_CFG.ORDEN;
  const colors = TIPO_COLORS[evento.tipo] || TIPO_COLORS.ORDEN;
  const hora   = evento.fecha?.includes('T') ? dayjs(evento.fecha).format('HH:mm') : null;

  return (
    <div
      onClick={() => irA(evento, nav)}
      style={{
        padding: '10px 14px', borderRadius: 10, background: '#fff',
        border: `1px solid ${colors.border}`, borderLeft: `4px solid ${colors.accent}`,
        cursor: 'pointer', marginBottom: 8, transition: 'box-shadow 0.2s',
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = commonStyles.shadowMd; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
        <span style={{ color: colors.accent, fontSize: 14 }}>{cfg.icon}</span>
        <span style={{ fontWeight: 700, fontSize: 13, color: hssColors.textPrimary }}>{evento.titulo}</span>
        {hora && <span style={{ fontSize: 11, color: hssColors.textSecondary, marginLeft: 'auto' }}>{hora}</span>}
      </div>
      {evento.ref && (
        <div style={{ fontSize: 12, color: hssColors.textSecondary, paddingLeft: 22 }}>{evento.ref}</div>
      )}
      {evento.descripcion && (
        <div style={{ fontSize: 12, color: hssColors.textSecondary, paddingLeft: 22, marginTop: 2 }}>{evento.descripcion}</div>
      )}
      <div style={{ display: 'flex', gap: 6, paddingLeft: 22, marginTop: 4, flexWrap: 'wrap' }}>
        <Tag color={cfg.antColor} style={{ fontSize: 10, margin: 0 }}>{cfg.label}</Tag>
        {evento.prioridad && evento.tipo === 'ORDEN' && (
          <Tag color={PRIORIDAD_COLOR[evento.prioridad]} style={{ fontSize: 10, margin: 0 }}>{evento.prioridad}</Tag>
        )}
        {evento.tipo === 'PROMESA_PAGO' && evento.estado && (
          <Tag color={ESTADO_PROMESA_COLOR[evento.estado] || 'default'} style={{ fontSize: 10, margin: 0 }}>{evento.estado}</Tag>
        )}
        {evento.tipo === 'CONTRATO_VENCE' && evento.dias_restantes !== undefined && (
          <Tag
            color={evento.dias_restantes < 0 ? 'red' : evento.dias_restantes <= 30 ? 'orange' : 'green'}
            style={{ fontSize: 10, margin: 0 }}
          >
            {evento.dias_restantes < 0
              ? `Vencido hace ${Math.abs(evento.dias_restantes)}d`
              : `Vence en ${evento.dias_restantes}d`}
          </Tag>
        )}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// VISTA DÍA
// ════════════════════════════════════════════════════════════════════════════════
const DayCalendarView = ({ date, eventos, navigate: nav }) => {
  const timelineRef = useRef(null);
  const dateStr = date.format('YYYY-MM-DD');
  const dayEvts = eventos.filter(e => (e.fecha || '').slice(0, 10) === dateStr);

  const getTop = (e) => {
    const h = getEvtHour(e);
    const m = getEvtMinute(e);
    return Math.max(0, ((h - FIRST_HOUR) * 60 + m) * (HOUR_HEIGHT / 60));
  };

  const now     = dayjs();
  const isToday = date.isSame(now, 'day');
  const nowTop  = isToday
    ? ((now.hour() - FIRST_HOUR) * 60 + now.minute()) * (HOUR_HEIGHT / 60)
    : null;

  useEffect(() => {
    if (!timelineRef.current) return;
    const targetHour = isToday
      ? now.hour()
      : dayEvts.length > 0 ? getEvtHour(dayEvts[0]) : FIRST_HOUR + 1;
    timelineRef.current.scrollTop = Math.max(0, (targetHour - FIRST_HOUR) * HOUR_HEIGHT - 32);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateStr]);

  return (
    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e9ecef', boxShadow: commonStyles.shadowMd, overflow: 'hidden' }}>
      {/* Timeline */}
      <div ref={timelineRef} style={{ display: 'flex', maxHeight: 560, overflowY: 'auto' }}>
        {/* Horas */}
        <div style={{ width: 56, flexShrink: 0, borderRight: '1px solid #f0f0f0', position: 'relative', height: TOTAL_HEIGHT }}>
          {HOURS_DAY.map((h, i) => (
            <div key={h} style={{ position: 'absolute', top: i * HOUR_HEIGHT, width: '100%' }}>
              <div style={{ textAlign: 'right', paddingRight: 8, paddingTop: 4, fontSize: 11, color: '#adb5bd' }}>{h}:00</div>
              <div style={{ position: 'absolute', top: HOUR_HEIGHT / 2, right: 8, fontSize: 9, color: '#ced4da' }}>{h}:30</div>
            </div>
          ))}
        </div>

        {/* Área de eventos */}
        <div style={{ flex: 1, position: 'relative', height: TOTAL_HEIGHT }}>
          {HOURS_DAY.map((_, i) => (
            <React.Fragment key={i}>
              <div style={{ position: 'absolute', width: '100%', top: i * HOUR_HEIGHT, borderTop: '1px solid #f0f0f0' }} />
              <div style={{ position: 'absolute', width: '100%', top: i * HOUR_HEIGHT + HOUR_HEIGHT / 2, borderTop: '1px dashed #f0f0f0' }} />
            </React.Fragment>
          ))}

          {/* Línea de hora actual */}
          {nowTop !== null && nowTop >= 0 && nowTop <= TOTAL_HEIGHT && (
            <div style={{ position: 'absolute', width: '100%', top: nowTop, zIndex: 20, display: 'flex', alignItems: 'center', pointerEvents: 'none' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', marginLeft: -4, flexShrink: 0 }} />
              <div style={{ flex: 1, height: 1, background: '#ef4444' }} />
            </div>
          )}

          {dayEvts.length === 0 && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
              <CalendarOutlined style={{ fontSize: 40, color: '#ced4da' }} />
              <div style={{ fontSize: 13, color: '#adb5bd' }}>Sin eventos este día</div>
            </div>
          )}

          {dayEvts.map(e => {
            const colors = TIPO_COLORS[e.tipo] || TIPO_COLORS.ORDEN;
            const cfg    = TIPO_CFG[e.tipo]    || TIPO_CFG.ORDEN;
            const top    = getTop(e);
            return (
              <div key={e.id}
                onClick={() => irA(e, nav)}
                style={{
                  position: 'absolute', top: top + 1, height: HOUR_HEIGHT - 2,
                  left: 4, right: 4, borderRadius: 8, cursor: 'pointer',
                  display: 'flex', overflow: 'hidden',
                  background: colors.bg, border: `1px solid ${colors.border}`, zIndex: 5,
                  transition: 'box-shadow 0.15s',
                }}
                onMouseEnter={e2 => { e2.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.12)'; }}
                onMouseLeave={e2 => { e2.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ width: 4, flexShrink: 0, background: colors.accent, borderRadius: '8px 0 0 8px' }} />
                <div style={{ flex: 1, padding: '4px 8px', minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden' }}>
                    <span style={{ color: colors.accent, fontSize: 11, flexShrink: 0 }}>{cfg.icon}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {String(getEvtHour(e)).padStart(2,'0')}:{String(getEvtMinute(e)).padStart(2,'0')} · {e.titulo}
                    </span>
                  </div>
                  {e.ref && (
                    <div style={{ fontSize: 10, color: '#6c757d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingLeft: 18 }}>
                      {e.ref}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// VISTA SEMANA
// ════════════════════════════════════════════════════════════════════════════════
const WeekCalendarView = ({ date, eventos, navigate: nav, onDateClick }) => {
  const monday = getMonday(date);
  const days   = Array.from({ length: 7 }, (_, i) => monday.add(i, 'day'));
  const today  = dayjs();

  const getEvtsForCell = (day, hour) => {
    const ds = day.format('YYYY-MM-DD');
    return eventos.filter(e => {
      if ((e.fecha || '').slice(0, 10) !== ds) return false;
      return getEvtHour(e) === hour;
    });
  };

  return (
    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e9ecef', boxShadow: commonStyles.shadowMd, overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: 720 }}>
          {/* Cabecera días */}
          <div style={{ display: 'grid', gridTemplateColumns: '56px repeat(7, 1fr)', borderBottom: '1px solid #e9ecef' }}>
            <div />
            {days.map(d => {
              const isTod = d.isSame(today, 'day');
              const isSel = d.isSame(date, 'day');
              return (
                <button key={d.format('YYYY-MM-DD')}
                  onClick={() => onDateClick(d)}
                  style={{ padding: 12, textAlign: 'center', background: isTod ? '#eff6ff' : 'transparent', border: 'none', cursor: 'pointer', transition: 'background 0.15s' }}
                >
                  <div style={{ fontSize: 10, color: '#6c757d', textTransform: 'uppercase', letterSpacing: 0.5 }}>{d.format('ddd')}</div>
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '4px auto 0',
                    background: isTod ? hssColors.primary : isSel ? '#dbeafe' : 'transparent',
                    color: isTod ? '#fff' : isSel ? hssColors.primary : '#212529',
                    fontWeight: 700, fontSize: 16,
                  }}>
                    {d.format('D')}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Filas de horas 07:00 - 19:00 */}
          <div style={{ maxHeight: 700, overflowY: 'auto' }}>
            {HOURS_WEEK.map(hour => (
              <div key={hour} style={{ display: 'grid', gridTemplateColumns: '56px repeat(7, 1fr)' }}>
                <div style={{ padding: '4px 8px 0 0', textAlign: 'right', fontSize: 11, color: '#adb5bd', borderRight: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0' }}>
                  {hour}:00
                </div>
                {days.map(d => {
                  const evts = getEvtsForCell(d, hour);
                  return (
                    <div key={d.format('YYYY-MM-DD') + hour}
                      onClick={() => onDateClick(d)}
                      style={{ minHeight: 60, padding: 4, borderRight: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0', cursor: 'pointer', transition: 'background 0.1s' }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#f8f9fa'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = ''; }}
                    >
                      {evts.map(e => {
                        const colors = TIPO_COLORS[e.tipo] || TIPO_COLORS.ORDEN;
                        const cfg    = TIPO_CFG[e.tipo]    || TIPO_CFG.ORDEN;
                        return (
                          <div key={e.id}
                            onClick={ev => { ev.stopPropagation(); irA(e, nav); }}
                            style={{ fontSize: 10, padding: '2px 6px', borderRadius: 6, marginBottom: 2, cursor: 'pointer', background: colors.bg, color: colors.text, border: `1px solid ${colors.border}`, borderLeft: `3px solid ${colors.accent}`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            onMouseEnter={e2 => { e2.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.1)'; }}
                            onMouseLeave={e2 => { e2.currentTarget.style.boxShadow = 'none'; }}
                          >
                            <span style={{ color: colors.accent, marginRight: 3 }}>{cfg.icon}</span>
                            {e.titulo}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// VISTA MES
// ════════════════════════════════════════════════════════════════════════════════
const MonthCalendarView = ({ date, eventos, onDateClick, fechaSeleccionada }) => {
  const today      = dayjs();
  const monthStart = date.startOf('month');
  const monthEnd   = date.endOf('month');
  const firstCell  = getMonday(monthStart);
  const lastDow    = monthEnd.day();
  const lastCell   = monthEnd.add(lastDow === 0 ? 0 : 7 - lastDow, 'day');

  const cells = [];
  let curr = firstCell;
  while (curr.isBefore(lastCell) || curr.isSame(lastCell, 'day')) {
    cells.push(curr);
    curr = curr.add(1, 'day');
  }

  const getEvtsForDay = (d) => {
    const ds = d.format('YYYY-MM-DD');
    return eventos.filter(e => (e.fecha || '').slice(0, 10) === ds);
  };

  return (
    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e9ecef', boxShadow: commonStyles.shadowMd, overflow: 'hidden' }}>
      {/* Cabecera días */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', background: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
        {DAYS_HEADER.map(d => (
          <div key={d} style={{ padding: '10px 0', textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#6c757d', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {d}
          </div>
        ))}
      </div>

      {/* Grilla */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {cells.map(d => {
          const isCurrentMonth = d.month() === date.month();
          const isTod = d.isSame(today, 'day');
          const isSel = fechaSeleccionada && d.isSame(fechaSeleccionada, 'day');
          const evts  = getEvtsForDay(d);

          return (
            <div key={d.format('YYYY-MM-DD')}
              onClick={() => onDateClick(d)}
              style={{
                minHeight: 90, padding: 6,
                borderRight: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0',
                cursor: 'pointer',
                background: isSel && !isTod ? '#eff6ff' : 'transparent',
                opacity: isCurrentMonth ? 1 : 0.38,
                transition: 'background 0.1s',
              }}
              onMouseEnter={e => { if (!isTod && !isSel) e.currentTarget.style.background = '#f8f9fa'; }}
              onMouseLeave={e => { e.currentTarget.style.background = isSel && !isTod ? '#eff6ff' : 'transparent'; }}
            >
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
                <div style={{
                  width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: isTod ? hssColors.primary : 'transparent',
                  color: isTod ? '#fff' : isCurrentMonth ? '#212529' : '#adb5bd',
                  fontSize: 13, fontWeight: isTod ? 700 : 500,
                  outline: isSel && !isTod ? `2px solid ${hssColors.primary}` : 'none',
                  outlineOffset: 1,
                }}>
                  {d.format('D')}
                </div>
              </div>

              {evts.slice(0, 3).map(e => {
                const colors = TIPO_COLORS[e.tipo] || TIPO_COLORS.ORDEN;
                return (
                  <div key={e.id} style={{
                    fontSize: 10, padding: '1px 5px', borderRadius: 4, marginBottom: 2,
                    background: colors.bg, color: colors.text, borderLeft: `2px solid ${colors.accent}`,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: '16px',
                  }}>
                    {e.titulo}
                  </div>
                );
              })}
              {evts.length > 3 && (
                <div style={{ fontSize: 10, color: '#6c757d', paddingLeft: 4 }}>+{evts.length - 3} más</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ════════════════════════════════════════════════════════════════════════════════
const SUB_VISTAS = [
  { value: 'dia',    label: 'Día' },
  { value: 'semana', label: 'Semana' },
  { value: 'mes',    label: 'Mes' },
];

const Agenda = () => {
  const navigate = useNavigate();
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('TODO');
  const [vista, setVista] = useState('calendario');       // 'calendario' | 'lista'
  const [subVista, setSubVista] = useState('mes');        // 'dia' | 'semana' | 'mes'
  const [fechaNav, setFechaNav] = useState(dayjs());      // fecha de navegación
  const [fechaSel, setFechaSel] = useState(dayjs());      // día seleccionado (panel mes)

  const cargar = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get('/agenda');
      setEventos(res.data || []);
    } catch {
      message.error('Error al cargar agenda');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const eventosFiltrados = eventos.filter(e => filtro === 'TODO' || e.tipo === filtro);

  // ── Navegación ──────────────────────────────────────────────────────────────
  const goToToday = () => { setFechaNav(dayjs()); setFechaSel(dayjs()); };

  const goBack = () => {
    setFechaNav(prev => {
      if (subVista === 'dia')    return prev.subtract(1, 'day');
      if (subVista === 'semana') return prev.subtract(1, 'week');
      return prev.subtract(1, 'month');
    });
  };

  const goForward = () => {
    setFechaNav(prev => {
      if (subVista === 'dia')    return prev.add(1, 'day');
      if (subVista === 'semana') return prev.add(1, 'week');
      return prev.add(1, 'month');
    });
  };

  const getTitle = () => {
    if (subVista === 'dia')    return fechaNav.format('dddd D [de] MMMM');
    if (subVista === 'semana') {
      const lun = getMonday(fechaNav);
      return `${lun.format('D MMM')} – ${lun.add(6, 'day').format('D MMM YYYY')}`;
    }
    return fechaNav.format('MMMM [de] YYYY');
  };

  // Desde grilla semana/mes → ir a vista día
  const handleDateClick = (d) => {
    setFechaNav(d);
    setFechaSel(d);
    setSubVista('dia');
  };

  // ── Vista Lista ─────────────────────────────────────────────────────────────
  const hoy = dayjs().format('YYYY-MM-DD');
  const eventosLista   = [...eventosFiltrados].sort((a, b) => new Date(a.fecha || 0) - new Date(b.fecha || 0));
  const eventosFuturos = eventosLista.filter(e => (e.fecha || '').slice(0, 10) >= hoy);
  const eventosPasados = eventosLista.filter(e => (e.fecha || '').slice(0, 10) <  hoy);

  // ── Filtros opciones ────────────────────────────────────────────────────────
  const filtroOpciones = [
    { value: 'TODO',           label: <Space><AppstoreOutlined />Todo</Space> },
    { value: 'ORDEN',          label: <Space><FileTextOutlined />Órdenes</Space> },
    { value: 'PROMESA_PAGO',   label: <Space><DollarOutlined />Promesas de pago</Space> },
    { value: 'CONTRATO_VENCE', label: <Space><FileProtectOutlined />Contratos</Space> },
  ];

  return (
    <div style={{ minHeight: '100vh', background: hssColors.backgroundPrimary, display: 'flex', flexDirection: 'column' }}>
      {/* ── Header (sin cambios) ── */}
      <div style={{ background: hssColors.backgroundSecondary, borderBottom: `1px solid ${hssColors.gray200}`, padding: '16px 32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <CalendarOutlined style={{ fontSize: 28, color: hssColors.primary }} />
            <div>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: hssColors.primary }}>Agenda</h1>
              <p style={{ margin: 0, fontSize: 13, color: hssColors.textSecondary }}>Órdenes programadas · Promesas de pago · Contratos</p>
            </div>
          </div>
          <Button icon={<HomeOutlined />} onClick={() => navigate('/dashboard')} size="large" style={{ height: 44, borderRadius: 8 }}>Inicio</Button>
        </div>
      </div>

      <div style={{ flex: 1, padding: '24px 32px' }}>
        {/* ── Stats (sin cambios) ── */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
          {[
            { label: 'Total eventos',        value: eventos.length,                                         icon: <CalendarOutlined />,    color: hssColors.primary },
            { label: 'Órdenes',              value: eventos.filter(e => e.tipo === 'ORDEN').length,          icon: <FileTextOutlined />,    color: hssColors.success },
            { label: 'Promesas de pago',     value: eventos.filter(e => e.tipo === 'PROMESA_PAGO').length,   icon: <DollarOutlined />,      color: '#faad14' },
            { label: 'Contratos por vencer', value: eventos.filter(e => e.tipo === 'CONTRATO_VENCE').length, icon: <FileProtectOutlined />, color: '#e67e22' },
          ].map(s => (
            <Card key={s.label} loading={loading} style={{ flex: 1, borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ fontSize: 28, color: s.color }}>{s.icon}</div>
                <div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: 12, color: hssColors.textSecondary }}>{s.label}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* ── Controles (sin cambios de diseño: Segmented + dos botones icono) ── */}
        <Card style={{ marginBottom: 20, borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }} styles={{ body: { padding: '16px 20px' } }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <Segmented options={filtroOpciones} value={filtro} onChange={setFiltro} size="large" />
            <Space>
              <Tooltip title="Vista calendario">
                <Button
                  type={vista === 'calendario' ? 'primary' : 'default'}
                  icon={<CalendarOutlined />}
                  onClick={() => setVista('calendario')}
                  style={{ borderRadius: 8 }}
                />
              </Tooltip>
              <Tooltip title="Vista lista">
                <Button
                  type={vista === 'lista' ? 'primary' : 'default'}
                  icon={<UnorderedListOutlined />}
                  onClick={() => setVista('lista')}
                  style={{ borderRadius: 8 }}
                />
              </Tooltip>
            </Space>
          </div>
        </Card>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '80px 0' }}><Spin size="large" /></div>
        ) : vista === 'calendario' ? (
          /* ── Vista Calendario (nueva) ── */
          <>
            {/* Barra con sub-selector Día/Semana/Mes + navegación */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: 16, background: '#fff', borderRadius: 12,
              padding: '10px 16px', boxShadow: commonStyles.shadowMd, flexWrap: 'wrap', gap: 12,
            }}>
              {/* Sub-selector de vista */}
              <div style={{ display: 'inline-flex', background: '#f0f0f0', borderRadius: 10, padding: 3 }}>
                {SUB_VISTAS.map(sv => (
                  <button key={sv.value}
                    onClick={() => setSubVista(sv.value)}
                    style={{
                      padding: '6px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
                      fontSize: 13, fontWeight: 500, transition: 'all 0.15s',
                      background: subVista === sv.value ? '#fff' : 'transparent',
                      color: subVista === sv.value ? hssColors.primary : '#6c757d',
                      boxShadow: subVista === sv.value ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                    }}
                  >
                    {sv.label}
                  </button>
                ))}
              </div>

              {/* Navegación */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Button icon={<LeftOutlined />} type="text" onClick={goBack} />
                <span style={{ fontWeight: 700, fontSize: 15, color: hssColors.primary, textTransform: 'capitalize', minWidth: 200, textAlign: 'center' }}>
                  {getTitle()}
                </span>
                <Button icon={<RightOutlined />} type="text" onClick={goForward} />
                <Button size="small" ghost type="primary" onClick={goToToday} style={{ borderRadius: 8, fontSize: 12, marginLeft: 4 }}>
                  Hoy
                </Button>
              </div>
            </div>

            {/* Vista Día */}
            {subVista === 'dia' && (
              <DayCalendarView date={fechaNav} eventos={eventosFiltrados} navigate={navigate} />
            )}

            {/* Vista Semana */}
            {subVista === 'semana' && (
              <WeekCalendarView date={fechaNav} eventos={eventosFiltrados} navigate={navigate} onDateClick={handleDateClick} />
            )}

            {/* Vista Mes */}
            {subVista === 'mes' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 20, alignItems: 'start' }}>
                <MonthCalendarView
                  date={fechaNav}
                  eventos={eventosFiltrados}
                  onDateClick={(d) => setFechaSel(d)}
                  fechaSeleccionada={fechaSel}
                />

                {/* Panel lateral */}
                <Card
                  title={
                    <Space>
                      <CalendarOutlined style={{ color: hssColors.primary }} />
                      <span style={{ fontWeight: 700 }}>{fechaSel.format('D [de] MMMM')}</span>
                      {(() => {
                        const n = eventosFiltrados.filter(e => (e.fecha || '').slice(0, 10) === fechaSel.format('YYYY-MM-DD')).length;
                        return n > 0 ? <Badge count={n} style={{ background: hssColors.primary }} /> : null;
                      })()}
                    </Space>
                  }
                  style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd, position: 'sticky', top: 96 }}
                >
                  {(() => {
                    const evtsDia = eventosFiltrados.filter(e => (e.fecha || '').slice(0, 10) === fechaSel.format('YYYY-MM-DD'));
                    return evtsDia.length === 0
                      ? <Empty description="Sin eventos este día" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                      : evtsDia.map(e => <EventoCard key={e.id} evento={e} navigate={navigate} />);
                  })()}
                </Card>
              </div>
            )}
          </>
        ) : (
          /* ── Vista Lista (sin cambios) ── */
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <Card
              title={<Space><ThunderboltOutlined style={{ color: hssColors.primary }} /><span>Próximos ({eventosFuturos.length})</span></Space>}
              style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }}
            >
              {eventosFuturos.length === 0 ? (
                <Empty description="Sin eventos próximos" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                  {eventosFuturos.map((e, i) => {
                    const fechaStr = dayjs(e.fecha).format('ddd D MMM');
                    const prev = eventosFuturos[i - 1];
                    const prevStr = prev ? dayjs(prev.fecha).format('ddd D MMM') : null;
                    return (
                      <div key={e.id}>
                        {fechaStr !== prevStr && (
                          <div style={{ fontSize: 11, fontWeight: 700, color: hssColors.textSecondary, textTransform: 'uppercase', letterSpacing: 1, padding: '8px 0 4px', marginTop: i > 0 ? 8 : 0 }}>
                            {fechaStr}
                          </div>
                        )}
                        <EventoCard evento={e} navigate={navigate} />
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>

            <Card
              title={<Space><CalendarOutlined style={{ color: hssColors.gray400 }} /><span style={{ color: hssColors.textSecondary }}>Pasados ({eventosPasados.length})</span></Space>}
              style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }}
            >
              {eventosPasados.length === 0 ? (
                <Empty description="Sin eventos pasados" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                  {[...eventosPasados].reverse().map((e, i) => {
                    const rev = [...eventosPasados].reverse();
                    const fechaStr = dayjs(e.fecha).format('ddd D MMM');
                    const prev = rev[i - 1];
                    const prevStr = prev ? dayjs(prev.fecha).format('ddd D MMM') : null;
                    return (
                      <div key={e.id}>
                        {fechaStr !== prevStr && (
                          <div style={{ fontSize: 11, fontWeight: 700, color: hssColors.textSecondary, textTransform: 'uppercase', letterSpacing: 1, padding: '8px 0 4px', marginTop: i > 0 ? 8 : 0 }}>
                            {fechaStr}
                          </div>
                        )}
                        <EventoCard evento={e} navigate={navigate} />
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Agenda;
